import { IUser, IRepository, IOrganization, INamespace } from '@/types';
import { RepositoryAuthorization } from './repositoryAuthorization';
import { OrganizationAuthorization } from './organizationAuthorization';
import { NamespaceAuthorization } from './namespaceAuthorization';
import { IConfig } from '../configApi';

export class Authorization {
  private user?: IUser;
  private config?: IConfig;

  constructor({ user, config }: { user?: IUser; config?: IConfig }) {
    this.user = user;
    this.config = config;
  }

  /**
   * Check if user has a specific role
   */
  private hasRole(role: string): boolean {
    return this.user?.role === role;
  }

  isLoggedIn(): boolean {
    return !!this.user?.username;
  }

  requiresVerification(): boolean {
    return !!this.config?.verify_email_on_signup_enabled && !this.user?.is_verified;
  }

  /**
   * Check if user is an admin
   */
  isOxenAdmin(): boolean {
    const hasAccess = this.hasRole('admin');
    return hasAccess;
  }

  /**
   * Create a repository-specific authorization checker
   */
  forRepository(repository?: IRepository): RepositoryAuthorization {
    return new RepositoryAuthorization({
      user: this.user,
      repository,
      awaitingUserVerification: this.requiresVerification(),
    });
  }

  /**
   * Create an organization-specific authorization checker
   */
  forOrganization(organization?: IOrganization): OrganizationAuthorization {
    return new OrganizationAuthorization({
      user: this.user,
      organization,
      awaitingUserVerification: this.requiresVerification(),
    });
  }

  /**
   * Create a namespace-specific authorization checker
   */
  forNamespace(namespace?: INamespace): NamespaceAuthorization {
    return new NamespaceAuthorization({
      user: this.user,
      namespace,
      awaitingUserVerification: this.requiresVerification(),
    });
  }

  currentUser(): IUser | undefined {
    return this.user;
  }
}
